import HttpService from './HttpService';

const formatError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.message || error.response?.data?.error || defaultMessage;
  const errorCode = error.response?.status;
  const details = error.response?.data?.details || {};
  // `errors` is the canonical structured-validation key (CLAUDE.md); the
  // backend dual-emits it alongside the legacy `details`. Expose both so
  // forms can surface field-level 400s.
  const errors = error.response?.data?.errors || [];

  if (process.env.NODE_ENV !== 'production') {
    console.error(defaultMessage, error);
  }

  return { message: errorMessage, code: errorCode, originalError: error, details, errors };
};

const ElectricalService = {
  // ------------------------------ Panel (parent) -----------------------------

  /**
   * List breaker panels for a house, each with its circuits embedded.
   * @param {number} houseId
   * @returns {Promise<{panels: Array}>}
   */
  getPanels: async (houseId) => {
    try {
      const response = await HttpService.get(`/houses/${houseId}/panels`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching panels');
    }
  },

  /** Get a single panel by id (circuits embedded). */
  getPanel: async (id) => {
    try {
      const response = await HttpService.get(`/panels/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching panel ${id}`);
    }
  },

  /** Create a panel under a house. JSON body. */
  createPanel: async (houseId, data) => {
    try {
      const response = await HttpService.post(`/houses/${houseId}/panels`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating panel');
    }
  },

  /** Update a panel. JSON body. */
  updatePanel: async (id, data) => {
    try {
      const response = await HttpService.put(`/panels/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating panel ${id}`);
    }
  },

  /** Delete a panel (cascades to its circuits). */
  deletePanel: async (id) => {
    try {
      const response = await HttpService.delete(`/panels/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting panel ${id}`);
    }
  },

  // ------------------------------ Circuit (child) ----------------------------

  /** List circuits for a panel. */
  getCircuits: async (panelId) => {
    try {
      const response = await HttpService.get(`/panels/${panelId}/circuits`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching circuits');
    }
  },

  /** Get a single circuit by id. */
  getCircuit: async (id) => {
    try {
      const response = await HttpService.get(`/circuits/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching circuit ${id}`);
    }
  },

  /** Create a circuit under a panel. JSON body. */
  createCircuit: async (panelId, data) => {
    try {
      const response = await HttpService.post(`/panels/${panelId}/circuits`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating circuit');
    }
  },

  /** Update a circuit. JSON body. */
  updateCircuit: async (id, data) => {
    try {
      const response = await HttpService.put(`/circuits/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating circuit ${id}`);
    }
  },

  /** Delete a circuit. */
  deleteCircuit: async (id) => {
    try {
      const response = await HttpService.delete(`/circuits/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting circuit ${id}`);
    }
  },
};

export default ElectricalService;
